import React, { useEffect, useId, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

const getFirstDefined = (...values) => {
    return values.find((value) => value !== undefined)
}

const normalizeTone = (value) => {
    const tone = String(value || '').trim().toLowerCase()

    if (['primary', 'danger', 'success', 'secondary', 'ghost'].includes(tone)) {
        return tone
    }

    return 'secondary'
}

const ConfirmDialog = ({
    open,
    isOpen,
    visible,

    title = 'Подтверждение',
    description,
    text,
    message,
    children,

    handleClose,
    onClose,
    onCancel,

    handleSubmit,
    onSubmit,
    onConfirm,

    confirmText = 'Подтвердить',
    cancelText = 'Отмена',
    confirmTone = 'primary',

    actions,
    showCancel = true,

    loading = false,
    saving = false,
    disabled = false,

    closeOnBackdrop = true,
    closeOnEscape = true,
    restoreFocus = true,

    role = 'alertdialog',
    size = 'md',
    className = '',
    panelClassName = '',
    zIndex,
}) => {
    const titleId = useId()
    const descriptionId = useId()
    const panelRef = useRef(null)
    const previousFocusRef = useRef(null)

    const shown = Boolean(getFirstDefined(open, isOpen, visible, false))
    const busy = Boolean(loading || saving)

    const closeHandler = onClose || onCancel || handleClose
    const submitHandler = onConfirm || onSubmit || handleSubmit
    const bodyText = getFirstDefined(description, text, message, '')

    // Parent forms recreate callbacks while typing. Keep current interaction
    // options without restarting the dialog's focus lifecycle on each render.
    const interactionRef = useRef(null)
    interactionRef.current = {
        busy,
        disabled,
        closeHandler,
        closeOnEscape,
        restoreFocus,
        role,
    }

    const resolvedActions = useMemo(() => {
        if (Array.isArray(actions) && actions.length) {
            return actions.filter(Boolean)
        }

        const result = []

        if (showCancel) {
            result.push({
                key: 'cancel',
                label: cancelText,
                tone: 'secondary',
                onClick: closeHandler,
            })
        }

        result.push({
            key: 'confirm',
            label: confirmText,
            tone: confirmTone,
            onClick: submitHandler,
            autoFocus: true,
        })

        return result
    }, [
        actions,
        cancelText,
        closeHandler,
        confirmText,
        confirmTone,
        showCancel,
        submitHandler,
    ])

    useEffect(() => {
        if (!shown || typeof document === 'undefined') return undefined

        previousFocusRef.current = document.activeElement

        const frame = window.requestAnimationFrame(() => {
            const panel = panelRef.current

            if (!panel) return

            const formField = interactionRef.current.role === 'dialog'
                ? panel.querySelector('input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')
                : null
            const preferred = panel.querySelector('[data-autofocus="true"]:not(:disabled)')
            const fallback = panel.querySelector('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')

            ;(formField || preferred || fallback || panel).focus?.()
        })

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                const current = interactionRef.current
                if (!current.closeOnEscape || current.busy || current.disabled) return

                event.preventDefault()
                current.closeHandler?.()
                return
            }

            if (event.key !== 'Tab') return

            const panel = panelRef.current
            if (!panel) return

            const focusable = Array.from(panel.querySelectorAll(
                'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
            )).filter((node) => node.getAttribute('aria-hidden') !== 'true')

            if (!focusable.length) {
                event.preventDefault()
                panel.focus()
                return
            }

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement

            if (event.shiftKey && active === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && active === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            window.cancelAnimationFrame(frame)
            document.removeEventListener('keydown', handleKeyDown)

            if (interactionRef.current.restoreFocus) {
                previousFocusRef.current?.focus?.()
            }
        }
    }, [shown])

    if (!shown || typeof document === 'undefined') return null

    const rootClassName = [
        'confirmDialog',
        `confirmDialog--${size}`,
        busy ? 'is-busy' : '',
        className,
    ].filter(Boolean).join(' ')

    const panelClasses = [
        'confirmDialog__panel',
        panelClassName,
    ].filter(Boolean).join(' ')

    const dialogNode = (
        <div
            className={rootClassName}
            style={zIndex ? { zIndex } : undefined}
            aria-hidden={!shown}
        >
            <button
                type="button"
                className="confirmDialog__backdrop"
                aria-label="Закрыть окно подтверждения"
                tabIndex={-1}
                onClick={() => {
                    if (!closeOnBackdrop || busy || disabled) return
                    closeHandler?.()
                }}
            />

            <section
                ref={panelRef}
                className={panelClasses}
                role={role}
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={bodyText ? descriptionId : undefined}
                aria-busy={busy}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="confirmDialog__content">
                    <h2 id={titleId} className="confirmDialog__title">
                        {title}
                    </h2>

                    {bodyText ? (
                        <div id={descriptionId} className="confirmDialog__description">
                            {bodyText}
                        </div>
                    ) : null}

                    {children ? (
                        <div className="confirmDialog__body">
                            {children}
                        </div>
                    ) : null}
                </div>

                <div className="confirmDialog__actions">
                    {resolvedActions.map((action, index) => {
                        const actionLoading = Boolean(action.loading)
                        const actionDisabled = Boolean(
                            disabled ||
                            busy ||
                            action.disabled
                        )
                        const tone = normalizeTone(action.tone)
                        const label = actionLoading && action.loadingLabel
                            ? action.loadingLabel
                            : action.label

                        return (
                            <button
                                key={action.key || `${tone}-${index}`}
                                type={action.type || 'button'}
                                className={[
                                    'confirmDialog__button',
                                    `confirmDialog__button--${tone}`,
                                    action.className || '',
                                ].filter(Boolean).join(' ')}
                                onClick={(event) => {
                                    event.stopPropagation()

                                    if (actionDisabled) return

                                    action.onClick?.(event)
                                }}
                                disabled={actionDisabled}
                                data-autofocus={action.autoFocus ? 'true' : undefined}
                            >
                                {actionLoading ? (
                                    <span className="confirmDialog__spinner" aria-hidden="true" />
                                ) : action.icon ? (
                                    <span className="confirmDialog__button-icon" aria-hidden="true">
                                        {action.icon}
                                    </span>
                                ) : null}

                                <span>{label}</span>
                            </button>
                        )
                    })}
                </div>
            </section>
        </div>
    )

    return createPortal(dialogNode, document.body)
}

export default React.memo(ConfirmDialog)
